<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item :to="{ name: 'DashboardHome' }">
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>異業點數</b-breadcrumb-item>
            <b-breadcrumb-item active>點數/商品兌換活動</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between mb-4 mb-xl-2">
        <h4 class="mb-2 mb-xl-0 font-weight-bold">點數/商品兌換活動</h4>
        <div class="col-12 col-xl-10 d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-xl-end">
          <b-button v-if="checkPermission([consts.PARTNER_MARKETING_EVENT_CREATE])" class="flex-shrink-0 mb-2 mb-xl-0" variant="primary" @click="goCreate">
            <i class="fa fa-plus"></i> 新增點數/商品兌換活動
          </b-button>
          <b-form-select class="ml-2" style="max-width: 120px;" :value="type" @input="updateFilter('type', $event)" :options="typeOptions" />
          <b-form-select class="ml-2" style="max-width: 120px;" :value="is_processing" @input="updateFilter('is_processing', $event)" :options="isProcessingOptions" />
          <b-form-select class="ml-2" style="max-width: 120px;" :value="status" @input="updateFilter('status', $event)" :options="statusOptions" />
          <b-input-group class="ml-0 ml-xl-4" style="max-width: 220px; flex: 1 1 0;">
            <b-form-input v-model="keyword" placeholder="請輸入名稱、序號或商品號碼" @keyup.enter="handleSearch" />
            <b-input-group-append>
              <b-button @click="handleSearch"><i class="fa fa-search"></i></b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-table
        striped
        hover
        responsive
        :items="list"
        :fields="fields"
        :busy="isLoading"
        show-empty
        sort-by="sort"
        :sort-desc="false"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #head(active_plans_count)="data">
          {{ data.label }}
          <BIconQuestionCircle
            class="d-inline-block ml-1"
            id="active-plans-count-tooltip"
          ></BIconQuestionCircle>
          <b-popover
            target="active-plans-count-tooltip"
            triggers="hover focus"
          >
            <div class="text-left">
              【1個活動需至少建立1筆方案，才能顯示於前台】
            </div>
          </b-popover>
        </template>
        <template #cell(image_url)="data">
          <b-img-lazy v-if="data.item.image_url" :src="data.item.image_url" style="width: 80px; height: 80px; object-fit: contain;" />
        </template>
        <template #cell(is_active)="data">
          <b-badge :variant="isActive(data.item) ? 'success' : 'warning'">
            {{ isActive(data.item) ? '上架' : '下架' }}
          </b-badge>
        </template>
        <template #cell(type)="data">
          <b-badge :variant="data.item.type === 'serial' ? 'primary' : 'success'">
            {{ data.item.type === 'serial' ? '序號' : data.item.type === 'product' ? '商品' : data.item.type }}
          </b-badge>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusMap[data.item.status] ? statusMap[data.item.status].variant : 'secondary'">
            {{ statusMap[data.item.status] ? statusMap[data.item.status].text : data.item.status }}
          </b-badge>
        </template>
        <template #cell(start_at)="data">
          {{ formatDate(data.item.start_at) }}
        </template>
        <template #cell(end_at)="data">
          {{ formatDate(data.item.end_at) }}
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="inverse-info" class="mr-1" :to="{ name: 'PartnerMarketingPlanListByEvent', params: { partnerMarketingEventId: data.item.id } }"><span class="mdi mdi-lead-pencil mr-1"></span>管理方案</b-button>
          <b-button size="sm" variant="inverse-primary" @click="goView(data.item)"><span class="mdi mdi-eye mr-1"></span>查看</b-button>
          <b-button v-if="checkPermission([consts.PARTNER_MARKETING_EVENT_MODIFY])" size="sm" variant="inverse-warning" class="ml-1" @click="goEdit(data.item)"><span class="mdi mdi-lead-pencil mr-1"></span>編輯</b-button>
          <template v-if="checkPermission([consts.PARTNER_MARKETING_EVENT_DELETE])">
            <span :id="`delete-btn-${data.item.id}`">
              <b-button
                size="sm"
                variant="inverse-danger"
                class="ml-1"
                :disabled="data.item.status !== 'closed'"
                @click="remove(data.item)"
              ><span class="mdi mdi-delete mr-1"></span>刪除</b-button>
            </span>
            <b-tooltip v-if="data.item.status !== 'closed'" :target="`delete-btn-${data.item.id}`" placement="top">發布狀態為關閉時，才能刪除</b-tooltip>
          </template>
        </template>
      </b-table>
      <CustomPagination
        :currentPage="page"
        :totalRows="pagination.total"
        :perPage="per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import partnerMarketingEvent from '@/apis/partnerMarketingEvent'
import { BBadge, BButton, BCard, BTable, BFormInput, BInputGroup, BInputGroupAppend, BImgLazy, BBreadcrumb, BBreadcrumbItem, BTooltip, BIconQuestionCircle, BPopover, BSpinner } from 'bootstrap-vue'
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue"
import { updateQueryFromRoute, updateUrlWithoutReload } from '@/utils/updateUrl'
import moment from 'moment'
import PermissionChecker from "@/utils/PermissionChecker"
import * as consts from "@/consts"

export default {
  components: { BBadge, BButton, BCard, BTable, BFormInput, BInputGroup, BInputGroupAppend, BImgLazy, BBreadcrumb, BBreadcrumbItem, BTooltip, BIconQuestionCircle, BPopover, BSpinner, CustomPagination },
  data() {
    return {
      consts,
      list: [],
      isLoading: false,
      initialized: false,
      keyword: '',
      pagination: {
        total: 0
      },
      fields: [
        { key: 'name', label: '活動名稱', sortable: true },
        { key: 'active_plans_count', label: '生效中方案' },
        { key: 'image_url', label: '圖片' },
        { key: 'type', label: '類型' },
        { key: 'is_active', label: '上架狀態' },
        { key: 'status', label: '狀態' },
        { key: 'start_at', label: '開始時間', sortable: true },
        { key: 'end_at', label: '結束時間', sortable: true },
        { key: 'actions', label: '管理', class: 'text-nowrap' },
        { key: 'sort', label: '排序', sortable: true }
      ],
      statusOptions: [
        { value: null, text: '全部狀態' },
        { value: 'draft', text: '草稿' },
        { value: 'public', text: '公開' },
        { value: 'closed', text: '關閉' }
      ],
      typeOptions: [
        { value: null, text: '全部類型' },
        { value: 'serial', text: '序號' },
        { value: 'product', text: '商品' }
      ],
      isProcessingOptions: [
        { value: null, text: '全部資料' },
        { value: true, text: '上架' },
        { value: false, text: '下架' }
      ],
      statusMap: {
        public: { text: '公開', variant: 'success' },
        draft: { text: '草稿', variant: 'warning' },
        closed: { text: '關閉', variant: 'secondary' }
      }
    }
  },
  computed: {
    // 從 URL 讀取 filter values
    status() {
      return this.$route.query.status || null
    },
    is_processing() {
      return this.$route.query.is_processing !== undefined ? this.$route.query.is_processing : null
    },
    type() {
      return this.$route.query.type || null
    },
    page() {
      return parseInt(this.$route.query.page, 10) || 1
    },
    per_page() {
      return parseInt(this.$route.query.per_page, 10) || 10
    }
  },
  async created() {
    // 從 URL 同步 keyword（two-way binding 需要）
    this.keyword = this.$route.query.keyword || ''
    // 不要在這裡設置 initialized，讓 watcher 的 immediate 執行
  },
  methods: {
    formatDate(val) {
      return val ? moment(val).format('YYYY-MM-DD') : ''
    },
    fetchList() {
      // 從 $route.query 讀取所有參數
      const page = this.page
      const per_page = this.per_page
      const keyword = this.$route.query.keyword || ''
      const status = this.status
      const is_processing = this.is_processing
      const type = this.type

      this.isLoading = true
      const params = {
        page,
        per_page,
        keyword,
        status,
        sort_by: 'sort',
        order_by: 'asc',
      }

      // 只有在值不是 null 時才加入參數
      if (is_processing !== null) {
        params.is_processing = is_processing
      }

      partnerMarketingEvent.getPartnerMarketingEvents(params).then(res => {
        let data = res.data.data
        // 只保留後端不支援的前端 filter
        if (type !== null) {
          data = data.filter(item => item.type === type)
        }
        this.list = data
        // 有前端 filter 時使用 filtered data 的長度，否則使用 API 的 meta.total
        if (type !== null) {
          this.pagination.total = data.length
        } else {
          this.pagination.total = res.data.meta ? parseInt(res.data.meta.total, 10) : 0
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    updateFilter(key, value) {
      // 只更新 URL，重置 page 為 1
      this.$router.push({
        query: {
          ...this.$route.query,
          [key]: value,
          page: 1
        }
      })
    },
    goCreate() {
      this.$router.push({ name: 'PartnerMarketingEventCreate' })
    },
    goView(row) {
      this.$router.push({ name: 'PartnerMarketingEventView', params: { partnerMarketingEventId: row.id } })
    },
    goEdit(row) {
      this.$router.push({ name: 'PartnerMarketingEventEdit', params: { partnerMarketingEventId: row.id } })
    },
    remove(row) {
      this.$bvModal.msgBoxConfirm('確定要刪除嗎？', { title: '提示', okVariant: 'danger', okTitle: '刪除', cancelTitle: '取消', footerClass: 'p-2', centered: true }).then(confirmed => {
        if (confirmed) {
          partnerMarketingEvent.deletePartnerMarketingEvent(row.id).then(() => {
            this.$bvToast.toast('刪除成功', { variant: 'success', solid: true })
            this.fetchList()
          })
        }
      })
    },
    isActive(item) {
      const now = new Date();
      const startAt = new Date(item.start_at);
      const endAt = new Date(item.end_at);
      return item.status === 'public' && startAt <= now && endAt >= now;
    },
    handlePageChange(page) {
      // 只更新 URL
      this.$router.push({
        query: {
          ...this.$route.query,
          page
        }
      })
    },
    handlePerPageChange(perPage) {
      // 只更新 URL，重置 page 為 1
      this.$router.push({
        query: {
          ...this.$route.query,
          per_page: perPage,
          page: 1
        }
      })
    },
    handleSearch() {
      // 只更新 URL，加入 keyword
      this.$router.push({
        query: {
          ...this.$route.query,
          keyword: this.keyword,
          page: 1
        }
      })
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    }
  },
  watch: {
    // 統一由 $route.query 處理所有變化
    '$route.query': {
      handler() {
        // 同步 keyword（two-way binding）
        this.keyword = this.$route.query.keyword || ''

        // 重新載入資料
        this.fetchList()

        // 第一次執行後設置 initialized
        if (!this.initialized) {
          this.initialized = true
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>
